import type { TableRowId } from "@fluentui/react-components";
import type { IConfigurationInfo, IUserDetail, IGroupDetail, EAppId, IGraphPickerItem } from "../../interfaces";
import { configurableApplication } from "../../interfaces";

/**
 * Returns SettingsData for the first item in the set assuming the item has the following value "{appID}:{ID}""
 * @param items 
 * @returns configuration info
 */
export function getSettingsDataFromSelectedItem(items: Set<TableRowId>, settingsData: IConfigurationInfo[]): IConfigurationInfo | undefined {
    if (items.size !== 1) {
        return undefined;
    }
    const item: TableRowId | undefined = items.values().next().value;
    const ids: string[] = item === undefined ? [] : String(item.valueOf()).split(':');
    if (ids.length !== 2) {
        return undefined;
    }

    return settingsData.find((settings) => settings.appId === ids[0] && settings.id === ids[1]);
}

/**
 * Returns the app display name for a given appId
 * @param appId 
 * @returns app display name
 */
export function getAppDisplayNameFromAppId(appId?: string): string {
    if (!appId) {
        return "";
    }

    return configurableApplication[appId as EAppId] ?? "";
}

/**
 * Returns the user and group details from the selected people
 * @param people
 * @returns userDetails and groupDetails split by type
 */
export function getUserAndGroupDetailsFromSelectedPeople(people: IGraphPickerItem[]): ({userDetails: IUserDetail[], groupDetails: IGroupDetail[]}) {
    const users: IUserDetail[] = [];
    const groups: IGroupDetail[] = [];

    people.forEach(p => {
        if (!p.id || !p.displayName) {
            return;
        }

        if (p.type === "group") {
            groups.push({
                type: "group",
                id: p.id,
                displayName: p.displayName,
                email: p.mail ?? undefined,
                description: p.description ?? undefined
            });
        } else {
            users.push({
                type: "user",
                id: p.id,
                displayName: p.displayName,
                email: p.mail
            });
        }
    });

    return ({ userDetails: users, groupDetails: groups });
}

/**
 * Returns true if the provided string is a valid URL
 * @param url - The string to validate
 * @returns boolean - True if the string is a valid URL, otherwise false
 */
export const isURLValid = (url: string): boolean => {
    const urlRegex = /^(https?:\/\/)([\w.-]+)(\.[a-zA-Z]{2,})(:\d{2,5})?(\/.*)?$/;
    return urlRegex.test(url);
};
